import { useEffect, useState } from "react";
import { Box, TextField } from "@mui/material";
import { Search } from "@mui/icons-material";
import CustomPagination, {
  CustomPaginationProps,
} from "./table/CustomPagination";
import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import { useSearchParams } from "react-router-dom";

export interface Filter {
  placeholder: string;
  options: ControlledSelectOption[];
  onChange: (value: string) => void;
  value?: string;
  placeholdeShow?: boolean;
  enabled?: boolean;
}

export interface FilterRowProps {
  // If this is set, it automatically enables the search bar
  search?: string;
  onSearchChanged?: (search: string) => void;
  filters?: Filter[];
  tablePaginationProps: CustomPaginationProps;
  bulkActions?: React.ReactNode;
}

const FilterRow = ({
  tablePaginationProps,
  onSearchChanged,
  filters,
  search,
  bulkActions,
}: FilterRowProps) => {
  const [_params, setSearchParams] = useSearchParams();
  const styles = useStyles();
  const [srch, setSrch] = useState<string>(search || "");

  // Keep the local input in sync when the search prop changes externally
  // (e.g. a "Clear filters" action resets it to ""). Typing settles the prop
  // to the same value via the debounced onSearchChanged, so this is a no-op
  // during normal input and won't disrupt the cursor.
  useEffect(() => {
    setSrch(search || "");
  }, [search]);

  const checkColorChange = () => {
    // eslint-disable-next-line
    const header = document.getElementById("header") as any;
    const distanceFromTop = header.getBoundingClientRect().top;
    if (distanceFromTop <= 0) {
      header.classList.add("sticky");
      return;
    }

    header.classList.remove("sticky");
  };

  useEffect(() => {
    addEventListener("scroll", checkColorChange);
    return () => {
      removeEventListener("scroll", checkColorChange);
    };
  }, []);

  return (
    <Box
      sx={styles.section}
      mt="10px"
      position="sticky"
      top="0"
      mx="-20px"
      px="20px"
      id="header"
    >
      <Box>
        {bulkActions}
        {onSearchChanged && (
          <TextField
            variant="standard"
            sx={styles.searchInput}
            size="small"
            placeholder="Search"
            type="search"
            value={srch}
            onChange={(e) => {
              const value = e.target.value;
              setSrch(value);
              onSearchChanged(value);
              setSearchParams((params) => {
                value ? params.set("search", value) : params.delete("search");
                return params;
              });
            }}
            InputProps={{
              startAdornment: <Search />,
              disableUnderline: true,
              sx: {
                padding: 0,
                margin: 0,
                border: "none",
              },
            }}
          />
        )}
        {(filters || []).map(
          (
            {
              placeholder,
              options,
              onChange,
              value,
              placeholdeShow = true,
              enabled = true,
            },
            i,
          ) =>
            enabled && (
              <ControlledSelect
                key={i}
                placeholder={placeholder}
                options={options}
                onChange={onChange}
                value={value}
                sx={{
                  ...styles.select,
                  "&.MuiSelect-filled": {
                    padding: "2px 32px 2px 8px",
                  },
                }}
                placeholdeShow={placeholdeShow}
              />
            ),
        )}
      </Box>
      <CustomPagination {...tablePaginationProps} />
    </Box>
  );
};

const useStyles = makeSxStyles(
  ({
    palette: {
      gray1,
      background: { paper },
    },
  }) => ({
    section: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: "40px",
      //marginY: "40px",
      flexDirection: "row",
      "&.sticky": {
        background: paper,
      },
    },
    searchInput: {
      borderRadius: "200px",
      backgroundColor: "#282A2D",
      border: "none",
      outline: "none",
      padding: "6px 12px",
      minWidth: "400px",
      ".MuiInput-input": {
        padding: 0,
        marginLeft: "10px",
        color: gray1,
        fontWeight: 500,
      },
      ".MuiSvgIcon-root": {
        color: gray1,
      },
    },
    select: {
      marginLeft: "10px",
    },
  }),
);

export default FilterRow;
